/**
 * Owned Media → Inicio: la información general del Lab (pedido del equipo,
 * 08/10/2026), sembrada con la PPT "OML Lead Onboarding". Admins y Leads del
 * apartado editan cada sección con el lápiz; el resto la lee.
 *
 * Las credenciales NO van acá (el backend rechaza lo que parece una clave):
 * están en el Vault.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { trpc } from '@/lib/trpc';
import { toastError } from '@/lib/toast-error';
import { formatDateTime } from '@/lib/utils';
import { SectionContentBoard } from '@/components/section-content/SectionContentBoard';
import type { OmlHomeSection } from '@/../../shared/oml-home';

const k = 'oml.home';

export function OmlHomePage() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const q = trpc.oml.home.get.useQuery(undefined, { staleTime: 60_000 });
  const save = trpc.oml.home.save.useMutation({
    // Se usa lo que devolvió el guardado (con su versión nueva): un refetch podía caer en
    // otro contenedor con la versión vieja y el próximo guardado daba un conflicto falso.
    onSuccess: (r) => { toast.success(t(`${k}.saved`, 'Inicio actualizado')); utils.oml.home.get.setData(undefined, (prev) => (prev ? { ...prev, home: r.home } : prev)); setEditing(null); },
    onError: (e) => {
      toastError(e, t(`${k}.save_failed`, 'No se pudo guardar'));
      // Conflicto: se cierra el editor. Con el borrador viejo abierto y la versión nueva
      // ya cargada, un segundo Guardar pisaría el cambio de la otra persona.
      if (e.data?.code === 'CONFLICT') { utils.oml.home.get.invalidate(); setEditing(null); }
    },
  });
  /** Índice de la sección en edición; `sections.length` = sección nueva. */
  const [editing, setEditing] = useState<number | null>(null);

  if (q.isLoading) return <div className="flex justify-center py-24"><WinclapLoader size={40} /></div>;
  if (q.error || !q.data) {
    return (
      <Alert variant="destructive" className="max-w-xl">
        <AlertTitle>{t(`${k}.load_error`, 'No se pudo cargar el Inicio')}</AlertTitle>
        <AlertDescription>{q.error?.message}</AlertDescription>
      </Alert>
    );
  }

  const { home, canEdit } = q.data;
  const sections = home.sections;
  const persist = (next: OmlHomeSection[]) => save.mutate({ sections: next, prevUpdatedAt: home.updatedAt });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-[#FF7F5C]">{t(`${k}.kicker`, 'Owned Media Lab')}</p>
          <h1 className="text-2xl font-bold tracking-tight">{t(`${k}.title`, 'Inicio')}</h1>
          <p className="text-sm text-muted-foreground">{t(`${k}.subtitle`, 'Dónde vive cada cosa, cómo trabajamos y quién es quién en el Lab.')}</p>
        </div>
        <div className="flex items-center gap-3">
          {home.updatedAt && <span className="text-xs text-muted-foreground">{t(`${k}.updated`, 'Actualizado {{at}} por {{by}}', { at: formatDateTime(home.updatedAt), by: home.updatedBy ?? '—' })}</span>}
          {canEdit && <Button size="sm" variant="outline" onClick={() => setEditing(sections.length)}><Plus className="h-4 w-4 mr-1" />{t(`${k}.add_section`, 'Agregar sección')}</Button>}
        </div>
      </div>

      <SectionContentBoard
        sections={sections}
        canEdit={canEdit}
        saving={save.isPending}
        onPersist={persist}
        audienceNote={t(`${k}.no_secrets`, 'Lo ve todo el apartado: nada de contraseñas ni códigos acá. Las credenciales van al Vault.')}
        editing={editing}
        onEditingChange={setEditing}
      />
    </div>
  );
}
